import React, {useState} from 'react';
import {v4 as uuid} from 'uuid';
import {Box, Stack, styled} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import ProjectShelfButton from '@contentlab/components/project-shelf-button';
import UpgradeButton from '@contentlab/components/upgrade-button';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useContentLabPageName from '@contentlab/hooks/use-content-lab-page-name';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import Divider from '@/components/divider';
import CreditBox from '../credit-box';

interface HeaderProps {
  testID?: string;
}

const HeaderContainer = styled(Box)(() => ({
  boxSizing: 'border-box',
  padding: '20px 24px 16px 24px',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%',
  background: 'transparent',
}));

function Header({testID = 'header'}: HeaderProps) {
  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {uiInteraction} = useUiInteractionEvent();
  const [presentationId, setPresentationId] = useState<string>(uuid());

  const pageName = useContentLabPageName();

  const onCreditBoxOpen = () => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'credit_box_opened',
      interaction_location: CONTENT_LAB_VIEW_NAMES.HEADER,
      interaction_id: interactionId,
    });
    viewPresented({
      presentation_id: presentationId,
      interaction_id: interactionId,
      view_name: CONTENT_LAB_VIEW_NAMES.CREDIT_BOX,
      view_type: 'popup',
      view_location: pageName,
      source: 'item_pressed',
    });
  };

  const onCreditBoxClose = () => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'credit_box_closed',
      interaction_location: CONTENT_LAB_VIEW_NAMES.HEADER,
      interaction_id: interactionId,
    });
    viewDismissed({
      presentation_id: presentationId,
      interaction_id: interactionId,
      reason: 'dismissed',
    });
    setPresentationId(uuid());
  };

  const onUpgradeButtonClick = (openType: string) => {
    uiInteraction({
      item_name: 'upgrade_button_clicked',
      interaction_location: CONTENT_LAB_VIEW_NAMES.HEADER,
      interaction_id: uuid(),
      interaction_details: openType,
    });
  };
  return (
    <HeaderContainer data-testid={testID}>
      <Stack
        direction="row"
        alignItems="center"
        gap="12px"
        sx={{margin: 0, padding: 0}}
      >
        <CreditBox onOpen={onCreditBoxOpen} onClose={onCreditBoxClose} />
        <UpgradeButton onClick={onUpgradeButtonClick} />
        <Divider vertical margin={8} height="20px" />
        <ProjectShelfButton />
      </Stack>
    </HeaderContainer>
  );
}

export default Header;
